import {
    IProcessClearStuckThreadEventMetadata,
    IProcessEventMetadata,
    ISystemEvent,
    ICRDataIssueEventMetadata,
    IZCAddedEventMetadata,
    IZCStatusChangedEventMetadata
} from 'src/app/shared/interfaces/system-event.interface';
import { PROCESS_DYNAMIC_PROPERTIES, PROCESS_DYNAMIC_PROPERTIES_NAMES } from '../constants';
import * as moment from 'moment';
import * as _ from 'lodash';
import { SystemEventType } from 'src/app/shared/enums/system-event-type-enum';
import { getPrettyZCStatus } from 'src/app/shared/enums/zoning-code-status';

export class ProcessEventService {
    private static addMetadataMessageIfValid<T>(
        headerParts: string[],
        value: T,
        message: string,
        predicate: (value: T) => boolean = currentValue =>
            currentValue !== undefined &&
            currentValue !== null &&
            (typeof currentValue !== 'string' || currentValue !== '')
    ): void {
        if (predicate(value)) {
            headerParts.push(message);
        }
    }

    static getMetadataStr(event: ISystemEvent<any>): string {
        if (!event) {
            return '';
        }
        switch (true) {
            case event.isProcessClearStuckThread:
                return ProcessEventService.getClearStuckThreadMetadataStr(
                    event.MetaData as IProcessClearStuckThreadEventMetadata
                );
            case event.EventType === SystemEventType.ZONING_CODE_ADDED:
                return ProcessEventService.getZoningCodeAddedMetadataStr(event.MetaData as IZCAddedEventMetadata);
            case event.EventType === SystemEventType.ZONING_CODE_STATUS_CHANGED:
                return ProcessEventService.getZoningCodeStatusChangedMetadataStr(
                    event.MetaData as IZCStatusChangedEventMetadata
                );
            case event.showJSONMetaData:
                return ProcessEventService.getFormattedJSON(event.MetaData);
            case event.EventType === SystemEventType.CR_DATA_ISSUE_ERROR:
                return ProcessEventService.getCRDataIssueMetadataStr(event.MetaData as ICRDataIssueEventMetadata);
            default:
                return ProcessEventService.getPropertiesChangesStr(event.MetaData as IProcessEventMetadata);
        }
    }

    static getPropertiesChangesStr(metaData: IProcessEventMetadata): string {
        let result = '';
        if (!metaData) {
            return result;
        }
        PROCESS_DYNAMIC_PROPERTIES.forEach(propertyName => {
            const propertyData = metaData[propertyName];
            if (propertyData) {
                result =
                    result +
                    `${PROCESS_DYNAMIC_PROPERTIES_NAMES[propertyName]} : ${
                        propertyData.oldValue ? propertyData.oldValue : 'empty'
                    } -> ${propertyData.newValue ? propertyData.newValue : 'empty'}`;
            }
        });

        return result;
    }

    static getClearStuckThreadMetadataStr(metaData: IProcessClearStuckThreadEventMetadata) {
        if (!metaData) {
            return '';
        }
        const durationStr = metaData.durationMs ? `${moment(metaData.durationMs).format('mm:ss')} min` : 'No data';
        const gridicsIds = !_.isEmpty(metaData.propertyRecords)
            ? metaData.propertyRecords.map(pr => pr.gridicsId).join(', ')
            : 'No data';
        return `Duration: ${durationStr}, Gridics Ids: ${gridicsIds}`;
    }

    static getFormattedJSON(metaData: any) {
        if (!metaData) {
            return '';
        }
        return JSON.stringify(metaData, null, '\t');
    }

    static getCRDataIssueMetadataStr(metaData: ICRDataIssueEventMetadata): string {
        if (!metaData) {
            return '';
        }

        const headerParts: string[] = [];
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.calculationResultId,
            `Calculation Result: ${metaData.calculationResultId}`
        );
        ProcessEventService.addMetadataMessageIfValid(headerParts, metaData.stateEnv, `State: ${metaData.stateEnv}`);
        ProcessEventService.addMetadataMessageIfValid(headerParts, metaData.groupId, `Group: ${metaData.groupId}`);
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.processId,
            `Process Id: ${metaData.processId}`
        );
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.activeProcesses,
            `Active processes: ${metaData.activeProcesses.length}`,
            value => _.isArray(value)
        );
        ProcessEventService.addMetadataMessageIfValid(headerParts, metaData.metaData, 'Metadata available');

        return headerParts.join(', ');
    }

    static getZoningCodeAddedMetadataStr(metaData: IZCAddedEventMetadata): string {
        if (!metaData) {
            return '';
        }

        const headerParts: string[] = [];
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.zoningCodeId,
            `Id: ${metaData.zoningCodeId}`
        );
        ProcessEventService.addMetadataMessageIfValid(headerParts, metaData.name, `Name: ${metaData.name}`);
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.status,
            `Status: ${getPrettyZCStatus(metaData.status)}`
        );
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.gridicsId,
            `Gridics Id: ${metaData.gridicsId}`
        );

        return headerParts.join(', ');
    }

    static getZoningCodeStatusChangedMetadataStr(metaData: IZCStatusChangedEventMetadata): string {
        if (!metaData) {
            return '';
        }

        const headerParts: string[] = [];
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.zoningCodeId,
            `Zoning Code Id: ${metaData.zoningCodeId}`
        );
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.oldStatus,
            `Old status: ${getPrettyZCStatus(metaData.oldStatus)}`
        );
        ProcessEventService.addMetadataMessageIfValid(
            headerParts,
            metaData.newStatus,
            `New status: ${getPrettyZCStatus(metaData.newStatus)}`
        );

        return headerParts.join(', ');
    }
}
